import { Injectable } from '@angular/core';
import { MaterialDemandInfosService } from '../materialdemandinfos.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { ImsErrorHandler } from 'src/app/_util/ims-error-handler';
import { AppService } from 'src/app/_services/app-service/logic/app.service';
import { CredentialsService } from 'src/app/_services/credentials/logic/credentials.service';
import { BehaviorSubject } from 'rxjs';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { MaterialDemand } from 'src/app/_model/material-demand';
import { MaterialDemandPosition } from 'src/app/_model/material-demand-position';
import { MaterialDelivery } from 'src/app/_model/material-delivery';
import { MaterialDemandsDaoService } from '../../dao/material-demand-dao.service';
import { MaterialDemandPositionsDaoService } from '../../dao/material-demand-position-dao.service';
import { MaterialDeliveriesDaoService } from '../../dao/material-delivery-dao.service';
import { IMSId } from 'src/app/_model/ims-object';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';

@Injectable()
export class MaterialDemandInfosLogicService
  implements MaterialDemandInfosService
{
  // Public Member
  public materialDemands$ = new BehaviorSubject<MaterialDemand[]>([]);
  public materialDemandPositions$ = new BehaviorSubject<
    MaterialDemandPosition[]
  >([]);
  public materialDeliveries$ = new BehaviorSubject<MaterialDelivery[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private translate: TranslateService,
    private authenticationService: AuthenticationService,
    private remoteActionService: RemoteActionService,
    private errorHandler: ImsErrorHandler,
    private servicejobsService: ServicejobsService,
    private materialDemandsDaoService: MaterialDemandsDaoService,
    private materialDemandPositionsDaoService: MaterialDemandPositionsDaoService,
    private materialDeliveriesDaoService: MaterialDeliveriesDaoService,
    private appParameterService: AppParameterService,
  ) {
    this.initialize();
  }

  public getMaterialDeliveryById(id: IMSId): MaterialDelivery {
    if (id === undefined) {
      return undefined;
    }

    return this.materialDeliveries$.getValue().find((delivery) => {
      if (
        delivery.id.imsId == id.imsId &&
        delivery.id.secondaryId == id.secondaryId
      ) {
        return true;
      }
      return false;
    });
  }

  public getMaterialDemandById(id: IMSId): MaterialDemand {
    if (id === undefined) {
      return undefined;
    }

    return this.materialDemands$.getValue().find((demand) => {
      if (
        demand.id.imsId == id.imsId &&
        demand.id.secondaryId == id.secondaryId
      ) {
        return true;
      }
      return false;
    });
  }

  public getMaterialDemandPositionById(id: IMSId): MaterialDemandPosition {
    if (id === undefined) {
      return undefined;
    }

    return this.materialDemandPositions$.getValue().find((demandPos) => {
      if (
        demandPos.id.imsId == id.imsId &&
        demandPos.id.secondaryId == id.secondaryId
      ) {
        return true;
      }
      return false;
    });
  }

  // Resetable
  public async reset(): Promise<void> {
    await this.materialDemandsDaoService.reset();
    await this.materialDemandPositionsDaoService.reset();
    await this.materialDeliveriesDaoService.reset();
  }
  public async clear(): Promise<void> {
    await this.materialDemandsDaoService.clear();
    await this.materialDemandPositionsDaoService.clear();
    await this.materialDeliveriesDaoService.clear();
    this.materialDemands$.next([]);
    this.materialDemandPositions$.next([]);
    this.materialDeliveries$.next([]);
  }

  // Syncable
  public async presync(): Promise<void> {}
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.loadRemoteMaterialDemandInfo(apiLevel, syncKey);
  }
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalMaterialDemandInfo();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }
  async initializeCompleted(): Promise<void> {}

  // Public Functions

  // Private Functions

  private async loadLocalMaterialDemandInfo() {
    try {
      this.logger.log(
        this.constructor.name,
        '- Load local Material Demand Info',
      );

      let matDemands: MaterialDemand[] =
        await this.materialDemandsDaoService.getMaterialDemands();
      this.materialDemands$.next(matDemands);
      this.logger.log(
        this.constructor.name,
        '- Loaded',
        matDemands.length,
        'local MaterialDemands',
      );

      let matDemandPositions: MaterialDemandPosition[] =
        await this.materialDemandPositionsDaoService.getMaterialDemandPositions();
      this.materialDemandPositions$.next(matDemandPositions);
      this.logger.log(
        this.constructor.name,
        '- Loaded',
        matDemandPositions.length,
        'local MaterialDemandPositions',
      );

      let matDeliveries: MaterialDelivery[] =
        await this.materialDeliveriesDaoService.getMaterialDeliveries();
      this.materialDeliveries$.next(matDeliveries);
      this.logger.log(
        this.constructor.name,
        '- Loaded',
        matDeliveries.length,
        'local MaterialDeliveries',
      );
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local Peripherals: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteMaterialDemandInfo(apiLevel: APILevel, syncKey: string) {
    this.logger.log(
      this.constructor.name,
      '- loadRemoteMaterialDemandInfo with APILevel',
      apiLevel,
    );
    await this.materialDeliveriesDaoService.clear();
    await this.materialDemandsDaoService.clear();
    await this.materialDemandPositionsDaoService.clear();
    if (apiLevel < APILevel.MATERIALDEMAND) {
      this.logger.log(
        this.constructor.name,
        '- Backend API is too low to load materialdemands init with no mat infos',
      );
      var materialDemands: MaterialDemand[] = [];
      var materialDemandPositions: MaterialDemandPosition[] = [];
      var materialDeliveries: MaterialDelivery[] = [];
      await this.materialDemandsDaoService.saveMaterialDemands(materialDemands);
      await this.materialDemandPositionsDaoService.saveMaterialDemandPositions(
        materialDemandPositions,
      );
      await this.materialDeliveriesDaoService.saveMaterialDeliveries(
        materialDeliveries,
      );
      await this.loadLocalMaterialDemandInfo();
      return;
    }
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(
        this.constructor.name,
        '- Load new MaterialDemandInfo from IMS',
      );

      var materialDemands: MaterialDemand[] = [];
      var materialDemandPositions: MaterialDemandPosition[] = [];
      var materialDeliveries: MaterialDelivery[] = [];
      try {
        const sjobList = this.servicejobsService.servicejobs$
          .getValue()
          .map((sjob) => {
            return sjob.getImsId();
          })
          .join(',');

        let materialDemandInfoData: any =
          await this.remoteActionService.sendRemoteAction(
            new RemoteAction({
              endpoint: 'syncmaterialdemandinfo',
              cachable: false,
              fifo: false,
              authenticationRequired: true,
              data: {
                sjoblist: sjobList,
                synckey: syncKey
              },
            }),
          );

        this.logger.log(
          this.constructor.name,
          'Downloaded MaterialDemandInfo Data:',
          materialDemandInfoData,
        );

        for (const materialDemandData of materialDemandInfoData?.[
          'materialdemands'
        ]) {
          materialDemands.push(
            MaterialDemand.fromDto(
              materialDemandData,
              undefined,
              this.appParameterService.getFormatLabelForObject(
                'ims/MaterialDemand',
              ),
            ),
          );
        }
        this.logger.log(
          this.constructor.name,
          'Downloaded MaterialDemands: ',
          materialDemands,
        );

        for (const materialDemandPositionData of materialDemandInfoData?.[
          'materialdemandpositions'
        ]) {
          materialDemandPositions.push(
            MaterialDemandPosition.fromDto(
              materialDemandPositionData,
              undefined,
              this.appParameterService.getFormatLabelForObject(
                'ims/MaterialDemandPosition',
              ),
            ),
          );
        }
        this.logger.log(
          this.constructor.name,
          'Downloaded MaterialDemandPositions: ',
          materialDemandPositions,
        );

        for (const materialDeliveryData of materialDemandInfoData?.[
          'materialdeliveries'
        ]) {
          materialDeliveries.push(
            MaterialDelivery.fromDto(
              materialDeliveryData,
              undefined,
              this.appParameterService.getFormatLabelForObject(
                'ims/MaterialDelivery',
              ),
            ),
          );
        }
        this.logger.log(
          this.constructor.name,
          'Downloaded MaterialDeliveries: ',
          materialDeliveries,
        );

        await this.materialDemandsDaoService.saveMaterialDemands(
          materialDemands,
        );
        await this.materialDemandPositionsDaoService.saveMaterialDemandPositions(
          materialDemandPositions,
        );
        await this.materialDeliveriesDaoService.saveMaterialDeliveries(
          materialDeliveries,
        );

        await this.loadLocalMaterialDemandInfo();
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading MaterialDemandInfo: ',
          error?.message,
        );
        throw error;
      }
    }
  }
}
